/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { filter, skip, startWith, switchMap, take, timeoutWith } from 'rxjs/operators';
import { Auth, user } from '@angular/fire/auth';
import { ReplaySubject, Subject, of } from 'rxjs';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Tenant, escapeEmail } from '@touchplan/common';
import { tenantGetterService } from '@touchplan/firebase-common';
import { fancyDatabase } from '@touchplan/client-firebase';
import firebase from 'firebase/compat/app';

/**
 * This service represents a "semi-live" connection to the email tenant index.
 * It checks once on load, and when specifically asked for after that.
 */

@Injectable({ providedIn: 'root' })
export class TenantMigrationNeeded {
	private auth = inject(Auth);
	//grumpy that the modular api can't be used here, despite instance that they're completely compatiable
	private angularFireDatabase: AngularFireDatabase = inject(AngularFireDatabase);

	private triggerRefresh$ = new Subject<void>();
	private migrationTenant$ = new ReplaySubject<Tenant | null>();

	/** emits the tenant when a migration is pending or null */
	public migrationPendingTenant$ = this.migrationTenant$.asObservable();

	private checkEmailTenant(email: string) {
		//in theory this can only happen while a user is in the process of signing for sso
		//so... abort, and trigger a recheck when the sso process is done
		if (!email) {
			return of(null as string);
		}

		const encodedEmail = escapeEmail(email);
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(
				fancyFirebaseGetters.getIndicesEmailTenantsPath(encodedEmail)
			)
			.child('pendingExclusiveClaim');

		//delibrately emit only once
		return this.angularFireDatabase.object<string>(ref).valueChanges().pipe(take(1));
	}

	/**
	 * force rechecking the emailTenantIndex. Otherwise only runs once per session
	 * returned observable makes a "best effort" attempt to emit an observable saying that we've checked
	 * */
	public forceEmailTenantRecheck() {
		this.triggerRefresh$.next();

		return this.migrationTenant$.pipe(skip(1), timeoutWith(1500, this.migrationTenant$), take(1));
	}

	constructor() {
		//not using something like a buffer/ debounce to avoid keeping an open connection to firebase
		this.triggerRefresh$
			.pipe(
				startWith(undefined as void), //can't wait until we can update rxjs and make this stupid "deprecated" type bug go away
				switchMap(() => {
					return user(this.auth).pipe(
						filter((user) => !!user),
						switchMap((user) => this.checkEmailTenant(user.email)),
						switchMap((rtdbTenantId) => {
							if (!rtdbTenantId) {
								return of(null);
							} else {
								return tenantGetterService.getTenantViaRtdbId(fancyDatabase, rtdbTenantId);
							}
						}),
						take(1)
					);
				})
			)
			.subscribe(this.migrationTenant$);
	}
}
